import * as moment from 'moment';
import { useMemo, useState, useEffect, CSSProperties, ReactNode } from 'react';
import { IWatrReimbursement } from '@nkr/models/index';
import { ReimbursementType } from '@nkr/enums/index';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, ValueGetterParams, RowDataChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Label, Modal, Button } from 'react-bootstrap';
import { GridWrapper } from '../styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { formatApproverDateTime, formatDateOnly } from './utils/approver-display.utils';
import { SsnDisplay } from './ssn-display';

const HIGH_DOLLAR_THRESHOLD = 5000;
const MANY_PAYMENTS_THRESHOLD = 3;

export type HighDollarPaymentInput = {
    id: number,
    amount: number,
    reimbursementType: number,
    date: string,
};

export const isCreditReturnPayment = (row: { amount?: number, reimbursementType?: number }): boolean => {
    if (row.reimbursementType === ReimbursementType.CreditReturnLostWageReimbursement) return true;
    return Number(row.amount || 0) < 0;
};

export const evaluateHighDollarPayments = (
    payments: HighDollarPaymentInput[],
    threshold: number = HIGH_DOLLAR_THRESHOLD
): {
    requestNet: number,
    effectiveById: { [id: number]: number },
    highDollarIds: number[],
    offsetIds: number[],
    creditReturnIds: number[],
} => {
    const effectiveById: { [id: number]: number } = {};
    const remaining: { [id: number]: number } = {};
    const creditReturnIds: number[] = [];

    payments.forEach(p => {
        const amount = Number(p.amount || 0);
        effectiveById[p.id] = amount;
        if (isCreditReturnPayment(p)) {
            creditReturnIds.push(p.id);
        } else if (amount > 0) {
            remaining[p.id] = amount;
        }
    });

    const positives = payments
        .filter(p => !isCreditReturnPayment(p) && Number(p.amount || 0) > 0)
        .slice()
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || a.id - b.id);

    const credits = payments
        .filter(p => isCreditReturnPayment(p))
        .slice()
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || a.id - b.id);

    credits.forEach(credit => {
        let toApply = Math.abs(Number(credit.amount || 0));
        if (toApply <= 0) return;

        const candidates = positives
            .filter(p => (remaining[p.id] || 0) > 0.004)
            .slice()
            .sort((a, b) => {
                const aExact = Math.abs((remaining[a.id] || 0) - toApply) < 0.005 ? 0 : 1;
                const bExact = Math.abs((remaining[b.id] || 0) - toApply) < 0.005 ? 0 : 1;
                if (aExact !== bExact) return aExact - bExact;
                const aBefore = String(a.date || '') <= String(credit.date || '') ? 0 : 1;
                const bBefore = String(b.date || '') <= String(credit.date || '') ? 0 : 1;
                if (aBefore !== bBefore) return aBefore - bBefore;
                return String(a.date || '').localeCompare(String(b.date || '')) || a.id - b.id;
            });

        candidates.forEach(pos => {
            if (toApply <= 0.004) return;
            const available = remaining[pos.id] || 0;
            const apply = Math.min(available, toApply);
            remaining[pos.id] = available - apply;
            toApply -= apply;
        });
    });

    positives.forEach(p => {
        effectiveById[p.id] = Math.max(0, Number((remaining[p.id] || 0).toFixed(2)));
    });

    const offsetIds = positives
        .filter(p => Number(p.amount || 0) >= threshold && effectiveById[p.id] < 0.005)
        .map(p => p.id);

    const highDollarIds: number[] = [];
    payments.forEach(p => {
        if (isCreditReturnPayment(p)) {
            return;
        }
        if ((effectiveById[p.id] || 0) >= threshold) {
            highDollarIds.push(p.id);
        }
    });

    const requestNet = Number(
        payments.reduce((sum, p) => sum + Number(p.amount || 0), 0).toFixed(2)
    );

    return { requestNet, effectiveById, highDollarIds, offsetIds, creditReturnIds };
};

export type AuditExceptionFlags = {
    missingConfirmation: boolean,
    missingApprover: boolean,
    complications: boolean,
    possibleDuplicate: boolean,
    highDollar: boolean,
    missingReimbursed: boolean,
    paidBeforeDonation: boolean,
    incompleteTaxId: boolean,
    incompleteSsn?: boolean,
    incompleteAddress?: boolean,
    manyPayments: boolean,
    typeMismatch: boolean,
};

export type DuplicatePeer = {
    id: number,
    requestId: number,
    date: string,
    amount: number,
    confirmationCode: string,
    donorAlias: string,
    fullName: string,
    email: string,
    phone: string,
    reimbursementType: number,
    centerAlias: string,
    weeksRequested?: string,
};

export type AuditDisplayRow = IWatrReimbursement & {
    exceptionFlags?: AuditExceptionFlags,
    exceptionLabels?: string,
    duplicatePeers?: DuplicatePeer[],
    relatedPayments?: DuplicatePeer[],
};

interface IProps {
    data: AuditDisplayRow[],
    yearEndMode: boolean,
    duplicateResolutions?: { [id: number]: 'not-duplicate' | 'confirmed' },
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onGridSizeChanged: (e: GridSizeChangedEvent) => void,
    onUpdateSidebarView: (id: number) => void,
    onResolveDuplicate?: (reimbursementId: number, resolution: 'not-duplicate' | 'confirmed') => void,
}

const hasDisplayableSsn = (row: IWatrReimbursement): boolean => {
    const digits = String(row.ssn == null ? '' : row.ssn).replace(/\D/g, '');
    return digits.length === 9 && digits !== '000000000';
};

type FlagBadge = {
    key: keyof AuditExceptionFlags,
    label: string,
};

const FLAG_BADGES: FlagBadge[] = [
    { key: 'missingConfirmation', label: 'No confirmation' },
    { key: 'possibleDuplicate', label: 'Possible duplicate' },
    { key: 'highDollar', label: 'High-dollar' },
    { key: 'complications', label: 'Complication' },
    { key: 'paidBeforeDonation', label: 'Pre-donation' },
    { key: 'incompleteSsn', label: 'Incomplete SSN' },
    { key: 'incompleteAddress', label: 'Incomplete address' },
    { key: 'manyPayments', label: 'Multiple payments' },
    { key: 'typeMismatch', label: 'Type mismatch' },
];

const flagBadgeStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    marginRight: 4,
    marginBottom: 3,
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.2,
    background: '#fff7ed',
    color: '#9a3412',
    border: '1px solid #fdba74',
    whiteSpace: 'nowrap',
};

const getActiveFlagBadges = (row: AuditDisplayRow, yearEndMode: boolean): FlagBadge[] => {
    const flags = row.exceptionFlags;
    if (!flags) return [];
    return FLAG_BADGES.filter(badge => {
        if ((badge.key === 'incompleteSsn' || badge.key === 'incompleteAddress') && !yearEndMode) return false;
        if (badge.key === 'incompleteSsn') return !!flags.incompleteSsn;
        if (badge.key === 'incompleteAddress') return !!flags.incompleteAddress;
        return !!flags[badge.key as keyof AuditExceptionFlags];
    });
};

const FindingsCell = (props: {
    row: AuditDisplayRow,
    yearEndMode: boolean,
    onOpen: (row: AuditDisplayRow) => void,
}) => {
    const badges = getActiveFlagBadges(props.row, props.yearEndMode);
    if (!badges.length) return <span style={{ color: '#94a3b8' }}>—</span>;

    return (
        <button
            type='button'
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onOpen(props.row);
            }}
            title='View findings details'
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                width: '100%',
                maxHeight: 52,
                overflow: 'hidden',
                padding: 0,
                margin: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
            }}
        >
            {badges.map(badge => (
                <span key={badge.key} style={flagBadgeStyle}>{badge.label}</span>
            ))}
        </button>
    );
};

const formatAuditWhoWhen = (name: string, dateText: string): string => {
    if (name && dateText) return `${name} · ${dateText}`;
    if (name) return name;
    if (dateText) return dateText;
    return '';
};

const resolveApproverForAuditGrid = (row: IWatrReimbursement): { who: string, when: string, emptyHint: string } => {
    const name = (row.approverName || '').trim();
    const when = formatApproverDateTime(row.approvedDateTime, true);
    if (name === 'Multiple') {
        return { who: 'Multiple', when: '', emptyHint: '' };
    }
    if (name || when) {
        return { who: name || (row.approverUserId ? `User #${row.approverUserId}` : ''), when, emptyHint: '' };
    }
    return {
        who: '',
        when: '',
        emptyHint: 'No approver stamp (expected if approved before 9/9/2026)'
    };
};

const resolveReimbursedForAuditGrid = (row: IWatrReimbursement): { who: string, when: string, emptyHint: string } => {
    const name = (row.reimbursedUserName || '').trim();
    const when = formatApproverDateTime(row.reimbursedDateTime, true)
        || formatDateOnly(row.reimbursedDateTime, true);
    const userId = row.reimbursedUserId != null ? Number(row.reimbursedUserId) : 0;

    if (name || userId > 0 || when) {
        return {
            who: name || (userId > 0 ? `User #${userId}` : ''),
            when,
            emptyHint: ''
        };
    }

    return {
        who: '',
        when: '',
        emptyHint: 'No reimbursed-by stamp'
    };
};

const renderWhoWhenCell = (who: string, when: string, emptyHint: string) => {
    if (!who && !when) {
        return <span style={{ color: '#94a3b8' }} title={emptyHint}>—</span>;
    }
    return (
        <div style={{ lineHeight: 1.25 }} title={[who, when].filter(Boolean).join(' · ')}>
            {who ? <div style={{ fontWeight: 600, color: '#212529' }}>{who}</div> : null}
            {when ? <div style={{ fontSize: 11, color: '#64748b' }}>{when}</div> : null}
            {!who && when ? <div style={{ fontSize: 10, color: '#94a3b8' }}>who unknown</div> : null}
        </div>
    );
};

const reimbursementTypeLabel = (type: number) => {
    switch (type) {
        case ReimbursementType.Wages:
            return 'Wages';
        case ReimbursementType.WagesForComplication:
            return 'Wages (Complication)';
        case ReimbursementType.CreditReturnLostWageReimbursement:
            return 'Credit Return (Wages)';
        case ReimbursementType.TravelAndLodging:
            return 'Expenses';
        case ReimbursementType.TravelAndLodgingForComplication:
            return 'Travel (Complication)';
        case ReimbursementType.DppReimbursement:
            return 'DPP';
        case ReimbursementType.DependentCare:
            return 'Dependent Care';
        case ReimbursementType.AdvancedReimbursement:
            return 'Adv Reimbursement';
        case ReimbursementType.AdvancedReimbursementPreOp:
            return 'Adv (Pre-Op)';
        case ReimbursementType.AdvancedReimbursementDonation:
            return 'Adv (Donation)';
        case ReimbursementType.InternationalWorkup:
            return 'International Workup';
        default:
            return String(type || '');
    }
};

const TypeLabelCell = (props: { type: number }) => (
    <span title={reimbursementTypeLabel(props.type)} style={{ color: '#212529', fontSize: 13 }}>
        {reimbursementTypeLabel(props.type)}
    </span>
);

type FlagDetail = {
    title: string,
    detail: string,
    value: string | ReactNode,
    payments?: DuplicatePeer[],
    currentPaymentId?: number,
    highlightPaymentIds?: number[],
    showRequest?: boolean,
    notesText?: string,
    statusById?: { [id: number]: string },
};

const formatWeeksDisplay = (weeksRequested?: string | null): string => {
    const raw = (weeksRequested || '').trim();
    if (!raw) return '—';
    const parts = raw.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
    if (parts.length === 0) return '—';
    if (parts.length === 1) return parts[0];
    return `${parts.length} (${parts.join(', ')})`;
};

const relatedPaymentsForRow = (row: AuditDisplayRow): DuplicatePeer[] => {
    if (row.relatedPayments && row.relatedPayments.length > 0) {
        return row.relatedPayments;
    }
    const payee = row.fullName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';
    return [{
        id: row.id,
        requestId: row.requestId,
        date: row.date,
        amount: Number(row.amount || 0),
        confirmationCode: row.confirmationCode || '',
        donorAlias: row.donorAlias || '',
        fullName: payee,
        email: row.email || '',
        phone: row.phone || '',
        reimbursementType: row.reimbursementType,
        centerAlias: row.centerAlias || '',
        weeksRequested: row.weeksRequested || '',
    }];
};

const rowToPeer = (row: AuditDisplayRow | DuplicatePeer): DuplicatePeer => ({
    id: row.id,
    requestId: row.requestId,
    date: row.date,
    amount: Number(row.amount || 0),
    confirmationCode: row.confirmationCode || '',
    donorAlias: ('donorAlias' in row ? row.donorAlias : '') || '',
    fullName: row.fullName || `${(row as AuditDisplayRow).firstName || ''} ${(row as AuditDisplayRow).lastName || ''}`.trim(),
    email: row.email || '',
    phone: row.phone || '',
    reimbursementType: row.reimbursementType,
    centerAlias: row.centerAlias || '',
    weeksRequested: ('weeksRequested' in row ? row.weeksRequested : '') || '',
});

const buildDuplicateContextPayments = (
    row: AuditDisplayRow,
    allRows: AuditDisplayRow[],
    yearEndMode: boolean
): { payments: DuplicatePeer[], highlightIds: number[] } => {
    const peers = row.duplicatePeers || [];
    const highlightIds = [row.id].concat(peers.map(p => p.id));
    const requestIds: { [id: number]: boolean } = { [row.requestId]: true };
    peers.forEach(p => { requestIds[p.requestId] = true; });

    const byId: { [id: number]: DuplicatePeer } = {};
    const add = (payment: DuplicatePeer) => {
        if (!byId[payment.id]) byId[payment.id] = payment;
    };

    relatedPaymentsForRow(row).forEach(add);
    peers.forEach(add);

    if (yearEndMode) {
        allRows.forEach(r => {
            if (!requestIds[r.requestId]) return;
            relatedPaymentsForRow(r).forEach(add);
        });
    } else {
        allRows.forEach(r => {
            if (requestIds[r.requestId]) add(rowToPeer(r));
        });
    }

    const payments = Object.keys(byId)
        .map(id => byId[Number(id)])
        .sort((a, b) => {
            const req = Number(a.requestId) - Number(b.requestId);
            if (req !== 0) return req;
            return String(b.date || '').localeCompare(String(a.date || ''));
        });

    return { payments, highlightIds };
};

const collectNotesText = (row: AuditDisplayRow, allRows: AuditDisplayRow[]): string => {
    const parts: string[] = [];
    const seen: { [key: string]: boolean } = {};
    const add = (text?: string | null) => {
        const trimmed = (text || '').trim();
        if (!trimmed || seen[trimmed]) return;
        seen[trimmed] = true;
        parts.push(trimmed);
    };

    add(row.comments);
    relatedPaymentsForRow(row).forEach(p => {
        const match = (allRows || []).find(r => r.id === p.id || r.requestId === p.requestId);
        if (match) add(match.comments);
    });
    (allRows || []).forEach(r => {
        if (r.requestId === row.requestId) add(r.comments);
    });

    return parts.join('\n\n');
};

const renderPaymentsInQuestion = (
    payments: DuplicatePeer[],
    opts?: {
        currentPaymentId?: number,
        highlightPaymentIds?: number[],
        showRequest?: boolean,
        statusById?: { [id: number]: string },
    }
) => {
    const highlight = (opts && opts.highlightPaymentIds) || [];
    const highlightSet: { [id: number]: boolean } = {};
    highlight.forEach(id => { highlightSet[id] = true; });
    const showRequest = !!(opts && opts.showRequest);
    const statusById = (opts && opts.statusById) || {};

    return (
        <div style={{ overflowX: 'auto', marginTop: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                    <tr style={{ background: '#fff7ed', textAlign: 'left' }}>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>#</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Status</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Pay ID</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Paid</th>
                        {showRequest ? <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Request</th> : null}
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Amount</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Weeks</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Type</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Confirmation</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Payee</th>
                        <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Center</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.map((payment, idx) => {
                        const isCurrent = opts && opts.currentPaymentId != null && payment.id === opts.currentPaymentId;
                        const needsVerification = !!highlightSet[payment.id];
                        let status = statusById[payment.id] || 'Reference';
                        let rowBg: string | undefined;
                        if (needsVerification) {
                            if (!statusById[payment.id]) status = 'Verification required';
                            rowBg = isCurrent ? '#ffedd5' : '#fef3c7';
                        } else if (statusById[payment.id]) {
                            status = statusById[payment.id];
                            if (status.indexOf('Offset') >= 0) rowBg = '#f0fdf4';
                        } else if (isCurrent) {
                            status = 'Selected';
                            rowBg = '#ffedd5';
                        }
                        return (
                            <tr key={payment.id} style={{ background: rowBg }}>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>{idx + 1}</td>
                                <td style={{
                                    padding: '8px 6px',
                                    borderBottom: '1px solid #e2e8f0',
                                    fontWeight: needsVerification || (status.indexOf('Offset') >= 0) ? 700 : 500,
                                    color: needsVerification ? '#9a3412' : (status.indexOf('Offset') >= 0 ? '#166534' : '#64748b'),
                                }}>
                                    {status}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 700 }}>
                                    #{payment.id}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                    {moment(payment.date).isValid() ? moment(payment.date).format('MM/DD/YYYY') : (payment.date || '—')}
                                </td>
                                {showRequest ? (
                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                        #{payment.requestId}
                                    </td>
                                ) : null}
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 700 }}>
                                    ${Number(payment.amount || 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                    {formatWeeksDisplay(payment.weeksRequested)}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                    {reimbursementTypeLabel(payment.reimbursementType)}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                    {payment.confirmationCode || '(none)'}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                    {payment.fullName || '—'}
                                </td>
                                <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                    {payment.centerAlias || '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const buildFlagDetails = (row: AuditDisplayRow, yearEndMode: boolean, allRows: AuditDisplayRow[]): FlagDetail[] => {
    const flags = row.exceptionFlags;
    if (!flags) return [];

    const details: FlagDetail[] = [];
    const amountText = `$${Number(row.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const payee = row.fullName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';

    if (flags.missingConfirmation) {
        details.push({
            title: 'No confirmation #',
            detail: 'Payment has no confirmation number to tie to bank evidence.',
            value: (row.confirmationCode || '').trim() || '(empty)',
        });
    }
    if (flags.possibleDuplicate) {
        const peers = row.duplicatePeers || [];
        const sameRequest = peers.some(p => p.requestId === row.requestId);
        const differentRequest = peers.some(p => p.requestId !== row.requestId);
        let riskNote = 'Matching amount plus email/phone/name found in this window.';
        if (differentRequest) {
            riskNote = 'Same amount and identity across different claims — higher double-pay risk.';
        } else if (sameRequest) {
            riskNote = 'Same claim with matching amount/identity and same confirmation — possible double post.';
        }
        const context = buildDuplicateContextPayments(row, allRows, yearEndMode);
        const requestCount = Object.keys(context.payments.reduce((acc, p) => {
            acc[p.requestId] = true;
            return acc;
        }, {} as { [id: number]: boolean })).length;
        details.push({
            title: 'Possible duplicate',
            detail: `${riskNote} Full reimbursement list for involved request${requestCount === 1 ? '' : 's'} is below; highlighted rows require verification.`,
            value: `This pay $${Number(row.amount || 0).toFixed(2)} · Email: ${row.email || '—'} · Phone: ${row.phone || '—'} · ${peers.length} peer payment${peers.length === 1 ? '' : 's'} · ${context.payments.length} payment${context.payments.length === 1 ? '' : 's'} shown`,
            payments: context.payments,
            currentPaymentId: row.id,
            highlightPaymentIds: context.highlightIds,
            showRequest: true,
        });
    }
    if (flags.highDollar) {
        const payments = relatedPaymentsForRow(row);
        const evaluation = evaluateHighDollarPayments(payments);
        const highlightPaymentIds = evaluation.highDollarIds.length > 0
            ? evaluation.highDollarIds
            : (evaluation.requestNet >= HIGH_DOLLAR_THRESHOLD ? payments.map(p => p.id) : []);
        const statusById: { [id: number]: string } = {};
        payments.forEach(p => {
            if (evaluation.offsetIds.indexOf(p.id) >= 0) {
                statusById[p.id] = 'Offset by credit return';
            } else if (highlightPaymentIds.indexOf(p.id) >= 0) {
                statusById[p.id] = 'Verification required';
            }
        });
        const notesText = collectNotesText(row, allRows);
        const rolledUp = yearEndMode && payments.length > 1;
        const hasCredits = evaluation.creditReturnIds.length > 0;
        details.push({
            title: 'High-dollar',
            detail: hasCredits
                ? `High-dollar applies only to reimbursement payments. Amounts fully offset by a credit return are excluded; remaining payment amounts at/above $${HIGH_DOLLAR_THRESHOLD.toLocaleString()} require verification. Credit returns are not high-dollar.`
                : (rolledUp
                    ? `Rolled-up request total meets or exceeds $${HIGH_DOLLAR_THRESHOLD.toLocaleString()}. Full reimbursement breakdown and notes are below.`
                    : `Amount meets or exceeds $${HIGH_DOLLAR_THRESHOLD.toLocaleString()} threshold. Full reimbursement context and notes are below.`),
            value: `Gross total: $${evaluation.requestNet.toFixed(2)} · Net after offsets shown in status · Threshold: $${HIGH_DOLLAR_THRESHOLD.toLocaleString()} · Weeks: ${formatWeeksDisplay(row.weeksRequested)} · Payee: ${payee}`,
            payments,
            currentPaymentId: row.id,
            highlightPaymentIds,
            showRequest: false,
            notesText,
            statusById,
        });
    }
    if (flags.complications) {
        details.push({
            title: 'Complications',
            detail: 'Reimbursement type is a complication payment.',
            value: reimbursementTypeLabel(row.reimbursementType),
        });
    }
    if (flags.paidBeforeDonation) {
        details.push({
            title: 'Paid before donation / unconfirmed',
            detail: 'Paid date is before donation date, or donation is unconfirmed/missing.',
            value: `Paid: ${moment(row.date).isValid() ? moment(row.date).format('MM/DD/YYYY') : '—'} · Donation: ${row.organDonated || '(unconfirmed / missing)'}`,
        });
    }
    if (flags.incompleteTaxId && yearEndMode) {
        const missing: string[] = [];
        if (flags.incompleteSsn) missing.push('SSN');
        if (flags.incompleteAddress) missing.push('mailing address');
        details.push({
            title: missing.length === 2 ? 'Incomplete SSN/address' : (flags.incompleteSsn ? 'Missing/invalid SSN' : 'Incomplete address'),
            detail: flags.incompleteSsn && !flags.incompleteAddress
                ? 'SSN is missing or not a valid 9-digit value (dev placeholder 999999999 is accepted).'
                : (!flags.incompleteSsn && flags.incompleteAddress
                    ? 'Mailing address is incomplete (needs address, city, state, and zip). SSN is present.'
                    : 'Tax identity fields are incomplete for year-end reporting.'),
            value: hasDisplayableSsn(row) ? (
                <span>
                    SSN: <SsnDisplay ssnDecrypted={String(row.ssn)} /> · Address: {[row.address, row.city, row.state, row.zip].filter(Boolean).join(', ') || '(incomplete)'}
                </span>
            ) : (
                `SSN: (missing/invalid) · Address: ${[row.address, row.city, row.state, row.zip].filter(Boolean).join(', ') || '(incomplete)'}`
            ),
        });
    }
    if (flags.manyPayments) {
        const payments = relatedPaymentsForRow(row);
        const total = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
        details.push({
            title: 'Many payments on request',
            detail: `Request #${row.requestId} has ${payments.length} payments in this window for ${payee}. Payments requiring verification are listed below.`,
            value: `Payee: ${payee} · Payments: ${payments.length} · Total: $${total.toFixed(2)}`,
            payments,
            currentPaymentId: row.id,
            highlightPaymentIds: payments.map(p => p.id),
            showRequest: false,
        });
    }
    if (flags.typeMismatch) {
        details.push({
            title: 'Type mismatch',
            detail: 'Reimbursement type does not align with linked weeks/travels signals.',
            value: `Type: ${reimbursementTypeLabel(row.reimbursementType)} · Weeks: ${row.weeksRequested || '—'} · Travels: ${row.travelsRequested || '—'}`,
        });
    }

    return details;
};

const YearEndReportGrid = (props: IProps) => {
    const [flagRow, setFlagRow] = useState<AuditDisplayRow | null>(null);

    useEffect(() => {
        if (!flagRow) return;
        const updated = (props.data || []).find(r => r.id === flagRow.id);
        if (updated) {
            setFlagRow(updated);
        }
    }, [props.data, props.duplicateResolutions]);

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const flagDetails = flagRow ? buildFlagDetails(flagRow, props.yearEndMode, props.data || []) : [];

    const cols: ColDef[] = useMemo(() => {
        const base: ColDef[] = [
            {
                field: 'rank',
                headerName: 'R#',
                headerComponentParams: { columnName: 'R#' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: false,
                width: 48,
                valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
            },
            {
                field: 'date',
                headerName: 'Paid',
                headerComponentParams: { columnName: 'Paid' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 96,
                valueGetter: (params: ValueGetterParams) => moment(params.data.date).isValid() ? moment(params.data.date).format('MM/DD/YYYY') : ''
            },
            {
                field: 'reimbursementType',
                headerName: 'Reimbursement Type',
                headerComponentParams: { columnName: 'Reimbursement Type' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 140,
                cellStyle: { display: 'flex', alignItems: 'center', lineHeight: 'normal', paddingTop: 4, paddingBottom: 4 },
                valueGetter: (params: ValueGetterParams) => reimbursementTypeLabel(params.data.reimbursementType),
                cellRendererFramework: (params: ICellRendererParams) =>
                    <TypeLabelCell type={params.data.reimbursementType} />
            },
            {
                field: 'fullName',
                headerName: 'Payee',
                headerComponentParams: { columnName: 'Payee' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 130,
                valueGetter: (params: ValueGetterParams) => {
                    const data = params.data as IWatrReimbursement;
                    return data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim();
                }
            },
            {
                field: 'donorAlias',
                headerName: 'Alias',
                headerComponentParams: { columnName: 'Alias' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 90,
            },
            {
                field: 'centerAlias',
                headerName: 'Center',
                headerComponentParams: { columnName: 'Center' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 80,
            },
            {
                field: 'donationOrganType',
                headerName: 'Organ',
                headerComponentParams: { columnName: 'Organ' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 72,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data = params.data as IWatrReimbursement;
                    if (data.donationOrganType == 1) {
                        return <Label bsStyle='warning' style={{ fontSize: '100%' }}>L</Label>;
                    }
                    return <Label bsStyle='danger' style={{ fontSize: '100%' }}>K</Label>;
                }
            },
            {
                field: 'confirmationCode',
                headerName: 'Confirmation #',
                headerComponentParams: { columnName: 'Confirmation #' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 140,
                hide: props.yearEndMode
            },
            {
                field: 'exceptionLabels',
                headerName: 'Findings',
                headerComponentParams: { columnName: 'Findings' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 240,
                minWidth: 180,
                cellStyle: { display: 'flex', alignItems: 'center', lineHeight: 'normal', paddingTop: 4, paddingBottom: 4 },
                cellRendererFramework: (params: ICellRendererParams) => {
                    const row = params.data as AuditDisplayRow;
                    if (!row) return <></>;
                    return (
                        <FindingsCell
                            row={row}
                            yearEndMode={props.yearEndMode}
                            onOpen={setFlagRow}
                        />
                    );
                }
            },
            {
                field: 'approverName',
                headerName: 'Approved by',
                headerComponentParams: { columnName: 'Approved by' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 130,
                hide: props.yearEndMode,
                valueGetter: (params: ValueGetterParams) => {
                    const resolved = resolveApproverForAuditGrid(params.data as IWatrReimbursement);
                    return formatAuditWhoWhen(resolved.who, resolved.when);
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    const resolved = resolveApproverForAuditGrid(params.data as IWatrReimbursement);
                    return renderWhoWhenCell(resolved.who, resolved.when, resolved.emptyHint);
                }
            },
            {
                field: 'reimbursedUserName',
                headerName: 'Reimbursed by',
                headerComponentParams: { columnName: 'Reimbursed by' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 130,
                hide: props.yearEndMode,
                valueGetter: (params: ValueGetterParams) => {
                    const resolved = resolveReimbursedForAuditGrid(params.data as IWatrReimbursement);
                    return formatAuditWhoWhen(resolved.who, resolved.when);
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    const resolved = resolveReimbursedForAuditGrid(params.data as IWatrReimbursement);
                    return renderWhoWhenCell(resolved.who, resolved.when, resolved.emptyHint);
                }
            },
            {
                field: 'organDonated',
                headerName: 'Donation',
                headerComponentParams: { columnName: 'Donation' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 96,
                valueGetter: (params: ValueGetterParams) => formatDateOnly(params.data.organDonated, true)
            },
            {
                field: 'ssn',
                headerName: 'SSN',
                headerComponentParams: { columnName: 'SSN' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 96,
                hide: !props.yearEndMode,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data = params.data as IWatrReimbursement;
                    if (!hasDisplayableSsn(data)) {
                        return <span style={{ color: '#94a3b8' }}>—</span>;
                    }
                    return <SsnDisplay ssnDecrypted={String(data.ssn)} fallback='—' />;
                }
            },
            {
                field: 'count',
                headerName: '# Payments',
                headerComponentParams: { columnName: '# Payments' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 88,
                hide: !props.yearEndMode
            },
            {
                field: 'weeksRequested',
                headerName: 'Weeks',
                headerComponentParams: { columnName: 'Weeks' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 80,
                hide: !props.yearEndMode,
                valueGetter: (params: ValueGetterParams) => (params.data.weeksRequested || '').trim() || '—'
            },
            {
                field: 'amount',
                headerName: 'Amount',
                headerComponentParams: { columnName: 'Amount' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: true,
                width: 100,
                cellRendererFramework: (params: ICellRendererParams) =>
                    <div style={{ textAlign: 'right', fontWeight: 600 }}>
                        ${Number(params.data.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
            },
            {
                colId: 'actions',
                field: 'actions',
                headerName: '',
                headerComponentParams: { columnName: '' },
                headerComponentFramework: SortableHeaderComponent,
                sortable: false,
                width: 72,
                pinned: 'right',
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data = params.data as IWatrReimbursement;
                    return <a
                        onClick={() => props.onUpdateSidebarView(data.requestId)}
                        className='btn btn-warning btn-xs'>
                        Audit
                    </a>
                }
            },
        ];
        return base;
    }, [props.yearEndMode, props.onUpdateSidebarView]);

    const rowData = useMemo(() => props.data, [props.data]);
    const payee = flagRow
        ? (flagRow.fullName || `${flagRow.firstName || ''} ${flagRow.lastName || ''}`.trim())
        : '';

    return (
        <>
            <GridWrapper id="grid-wrapper" className='container-fluid-max-sm ag-theme-bootstrap' style={{ height: '70vh' }}>
                <AgGridReact
                    defaultColDef={{
                        sortable: true
                    }}
                    columnDefs={cols}
                    rowData={rowData}
                    headerHeight={52}
                rowHeight={48}
                suppressCellSelection={true}
                rowStyle={{ overflow: 'visible' }}
                noRowsOverlayComponentFramework={() => <div>No reimbursements found...</div>}
                onGridReady={props.onGridReady}
                onGridSizeChanged={props.onGridSizeChanged}
                onFirstDataRendered={props.onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
            />
            </GridWrapper>

            <Modal
                show={!!flagRow}
                onHide={() => setFlagRow(null)}
                bsSize='large'
                className='modal-vertical-center'
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Findings details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {flagRow && (
                        <>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 12,
                                marginBottom: 14,
                                padding: 10,
                                background: '#fafbfc',
                                border: '1px solid #e5eaf0',
                                borderRadius: 6,
                            }}>
                                <div style={{ minWidth: 140 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Payee</div>
                                    <div style={{ fontWeight: 600 }}>{payee || '—'}</div>
                                </div>
                                <div style={{ minWidth: 100 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Alias</div>
                                    <div style={{ fontWeight: 600 }}>{flagRow.donorAlias || '—'}</div>
                                </div>
                                <div style={{ minWidth: 90 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Request</div>
                                    <div style={{ fontWeight: 600 }}>#{flagRow.requestId}</div>
                                </div>
                                <div style={{ minWidth: 100 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Paid</div>
                                    <div style={{ fontWeight: 600 }}>{moment(flagRow.date).isValid() ? moment(flagRow.date).format('MM/DD/YYYY') : '—'}</div>
                                </div>
                                <div style={{ minWidth: 100 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Amount</div>
                                    <div style={{ fontWeight: 700 }}>${Number(flagRow.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                                <div style={{ minWidth: 120 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Type</div>
                                    <div style={{ fontWeight: 600 }}>{reimbursementTypeLabel(flagRow.reimbursementType)}</div>
                                </div>
                                <div style={{ minWidth: 90 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Center</div>
                                    <div style={{ fontWeight: 600 }}>{flagRow.centerAlias || '—'}</div>
                                </div>
                                <div style={{ minWidth: 120 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Confirmation</div>
                                    <div style={{ fontWeight: 600 }}>{flagRow.confirmationCode || '—'}</div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                                {getActiveFlagBadges(flagRow, props.yearEndMode).map(badge => (
                                    <span key={badge.key} style={flagBadgeStyle}>{badge.label}</span>
                                ))}
                            </div>

                            {flagDetails.length === 0 ? (
                                <div>No findings details available.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {flagDetails.map((item) => (
                                        <div
                                            key={item.title}
                                            style={{
                                                background: '#fff',
                                                border: '1px solid #e5eaf0',
                                                borderRadius: 6,
                                                padding: '10px 12px',
                                            }}
                                        >
                                            <div style={{ fontWeight: 700, color: '#333', marginBottom: 4 }}>{item.title}</div>
                                            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{item.detail}</div>
                                            <div style={{
                                                fontSize: 12,
                                                fontFamily: 'Consolas, Monaco, monospace',
                                                color: '#212529',
                                                background: '#fafbfc',
                                                border: '1px solid #e5eaf0',
                                                borderRadius: 4,
                                                padding: '8px 10px',
                                                whiteSpace: 'pre-wrap',
                                                marginBottom: item.payments && item.payments.length > 0 ? 10 : 0,
                                            }}>
                                                {item.value}
                                            </div>
                                            {item.payments && item.payments.length > 0 && renderPaymentsInQuestion(item.payments, {
                                                currentPaymentId: item.currentPaymentId,
                                                highlightPaymentIds: item.highlightPaymentIds,
                                                showRequest: item.showRequest,
                                                statusById: item.statusById,
                                            })}
                                            {item.notesText !== undefined && (
                                                <div style={{
                                                    marginTop: 10,
                                                    fontSize: 12,
                                                    color: '#0f172a',
                                                    background: '#fff7ed',
                                                    border: '1px solid #fed7aa',
                                                    borderRadius: 6,
                                                    padding: '8px 10px',
                                                    whiteSpace: 'pre-wrap',
                                                }}>
                                                    <div style={{ fontWeight: 700, color: '#9a3412', marginBottom: 6 }}>Notes</div>
                                                    {item.notesText && item.notesText.trim()
                                                        ? item.notesText
                                                        : '(no notes on this request)'}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(flagRow.duplicatePeers || []).length > 0 && (
                                <div style={{
                                    marginTop: 14,
                                    background: '#fff',
                                    border: '1px solid #fdba74',
                                    borderRadius: 10,
                                    padding: 12,
                                }}>
                                    <div style={{ fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>
                                        Live duplicate comparison
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                                        Match requires same amount plus email, phone, or full name.
                                        Same-claim payments with different confirmation #s are treated as normal split pays and are not flagged.
                                        {props.duplicateResolutions && props.duplicateResolutions[flagRow.id]
                                            ? ` Resolution: ${props.duplicateResolutions[flagRow.id] === 'not-duplicate' ? 'Not a duplicate' : 'Confirmed duplicate'}.`
                                            : ''}
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                            <thead>
                                                <tr style={{ background: '#fff7ed', textAlign: 'left' }}>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Row</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Paid</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Request</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Amount</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Name</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Email</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Phone</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Confirmation</th>
                                                    <th style={{ padding: '8px 6px', borderBottom: '1px solid #fed7aa' }}>Verdict</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 700 }}>This</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>{moment(flagRow.date).isValid() ? moment(flagRow.date).format('MM/DD/YYYY') : '—'}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>#{flagRow.requestId}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 700 }}>${Number(flagRow.amount || 0).toFixed(2)}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>{payee || '—'}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>{flagRow.email || '—'}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>{flagRow.phone || '—'}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>{flagRow.confirmationCode || '—'}</td>
                                                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>Current</td>
                                                </tr>
                                                {(flagRow.duplicatePeers || []).map(peer => {
                                                    const sameRequest = peer.requestId === flagRow.requestId;
                                                    const sameConf = (peer.confirmationCode || '') === (flagRow.confirmationCode || '') && !!(peer.confirmationCode || flagRow.confirmationCode);
                                                    const emailMatch = !!(flagRow.email && peer.email && String(flagRow.email).trim().toLowerCase() === String(peer.email).trim().toLowerCase());
                                                    const phoneDigits = (v?: string) => (v || '').replace(/\D/g, '').slice(-10);
                                                    const phoneMatch = !!(phoneDigits(flagRow.phone) && phoneDigits(peer.phone) && phoneDigits(flagRow.phone) === phoneDigits(peer.phone));
                                                    const nameMatch = !!(payee && peer.fullName && payee.trim().toLowerCase() === peer.fullName.trim().toLowerCase());
                                                    let verdict = 'Review';
                                                    if (!sameRequest) verdict = 'Different claim — risk';
                                                    else if (sameConf) verdict = 'Same conf on same claim — risk';
                                                    return (
                                                        <tr key={peer.id}>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>Peer #{peer.id}</td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0' }}>
                                                                {moment(peer.date).isValid() ? moment(peer.date).format('MM/DD/YYYY') : '—'}
                                                            </td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', background: sameRequest ? '#ecfccb' : '#fee2e2' }}>
                                                                #{peer.requestId}{sameRequest ? ' (same)' : ' (diff)'}
                                                            </td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 700 }}>${Number(peer.amount || 0).toFixed(2)}</td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', background: nameMatch ? '#ecfccb' : undefined }}>{peer.fullName || '—'}</td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', background: emailMatch ? '#ecfccb' : undefined }}>{peer.email || '—'}</td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', background: phoneMatch ? '#ecfccb' : undefined }}>{peer.phone || '—'}</td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', background: sameConf ? '#fee2e2' : undefined }}>
                                                                {peer.confirmationCode || '—'}
                                                            </td>
                                                            <td style={{ padding: '8px 6px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, color: verdict.indexOf('risk') >= 0 ? '#b91c1c' : '#166534' }}>
                                                                {verdict}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e2e8f0', background: '#fff' }}>
                    {flagRow && (flagRow.duplicatePeers || []).length > 0 && props.onResolveDuplicate && (
                        <>
                            <Button
                                bsStyle='success'
                                onClick={() => {
                                    props.onResolveDuplicate(flagRow.id, 'not-duplicate');
                                    setFlagRow(null);
                                }}
                            >
                                Not a duplicate
                            </Button>
                            <Button
                                bsStyle='danger'
                                onClick={() => {
                                    props.onResolveDuplicate(flagRow.id, 'confirmed');
                                }}
                            >
                                Confirm duplicate
                            </Button>
                        </>
                    )}
                    {flagRow && (
                        <Button
                            bsStyle='warning'
                            onClick={() => {
                                const requestId = flagRow.requestId;
                                setFlagRow(null);
                                props.onUpdateSidebarView(requestId);
                            }}
                        >
                            Open Audit
                        </Button>
                    )}
                    <Button onClick={() => setFlagRow(null)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default YearEndReportGrid;
